import {useContext, useEffect, useMemo, useState, type ReactNode} from "react";
import {Button, Drawer, Modal, Switch, TextInput, ActionIcon} from "@mantine/core";
import {FloppyDiskIcon, FolderOpenIcon, PencilIcon, PlusIcon, TrashIcon, ShareIcon, UploadSimpleIcon} from "@phosphor-icons/react";
import {useNavigate, useParams} from "react-router";
import api, {useUser} from "../../utils/api.ts";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import MessageContext from "../../context/MessageContext.tsx";
import {Col, Row} from "../Flex/Flex.tsx";
import Label from "../Label/Label.tsx";
import Premium from "../../utils/Premium.ts";
import SplitButton from "../SplitButton/SplitButton.tsx";

import {useSaveFitout} from "./useSaveFitout.ts";
import useSavedCount from "./useSavedCount.ts";

const DEFAULT_LABELS: SharedSaveLabels = {
    item: 'item',
    create: 'Create',
    save: 'Save',
    duplicate: 'Duplicate',
    load: 'Load',
    createNew: 'Create new',
    updateTitle: 'Update item',
    createTitle: 'Create new item',
    deleteConfirm: 'Are you sure you want to delete the current item?',
    deletedSuccess: 'Item deleted',
    emptyList: 'No elements available',
    loadedListTitle: 'Load'
};

interface SaveModalState {
    show: boolean;
    id: string;
}

interface SaveActionOption {
    label: string;
    icon: ReactNode;
    disabled?: boolean;
    onClick: () => void;
}

export default function SharedSave({
    type,
    content,
    savedData,
    itemPath,
    maxSavedItems = Premium.FITOUTS_SAVED,
    labels: labelsProp
}: SharedSaveProps) {
    const queryClient = useQueryClient();
    const {id} = useParams();
    const {user} = useUser()
    const count = useSavedCount(type);
    const [showSave, setShowSave] = useState<SaveModalState>({show: false, id: ''});
    const [showLoad, setShowLoad] = useState<boolean>(false);
    const navigate = useNavigate();
    const {success, error} = useContext(MessageContext);
    const labels = {...DEFAULT_LABELS, ...labelsProp};
    const basePath = itemPath ?? `/cargo/${type}`;

    const isOverUse = !(user.subLevel > 0) && (typeof count === 'undefined' || count > maxSavedItems);

    const isOwned = savedData?.owner === user.uid;

    const options = useMemo<SaveActionOption[]>(() => {
        if (savedData?.id && user.uid === savedData.owner) {
            return [
                {
                    label: labels.createNew,
                    icon: <PlusIcon size={16}/>,
                    disabled: isOverUse,
                    onClick: () => setShowSave({show: true, id: ''})
                },
                {
                    label: 'Share',
                    icon: <ShareIcon size={16}/>,
                    disabled: !savedData?.isPublic,
                    onClick: () => {
                        navigator.clipboard.writeText(location.href).then(() => {
                            success('URL copied to clipboard');
                        }).catch(e => {
                            error('Could not copy URL to clipboard', e.message);
                        })
                    }
                },
                {
                    label: 'Edit',
                    icon: <PencilIcon size={16}/>,
                    onClick: () => setShowSave({show: true, id: savedData.id})
                },
                {
                    label: 'Delete',
                    icon: <TrashIcon size={16}/>,
                    onClick: () => {
                        if (confirm(labels.deleteConfirm)) {
                            deleteMutation.mutate(savedData.id)
                            navigate(basePath);
                        }
                    }
                }
            ]
        } else {
            return []
        }
    }, [savedData, user, isOverUse, basePath, labels]);


    const saveMutation = useSaveFitout(type, id, basePath);

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            return await api.delete(`/persistence/${type}/${id}`)
        },
        onSuccess: () => {
            // navigate to default view
            success(labels.deletedSuccess);
            queryClient.invalidateQueries({
                queryKey: ['persistence', type]
            })
            queryClient.invalidateQueries({
                queryKey: ['persistence', type, 'count']
            })
            navigate(basePath);
        },
        onError: err => {
            error('Error deleting record', err.message);
        }
    })

    return (
        <>
            <Row>
            {id ? (
                <SplitButton
                    leftSection={<FloppyDiskIcon size={16}/>}
                    disabled={isOverUse && !(!!id && isOwned)}
                    onClick={() => {
                        if (id && isOwned) {
                            saveMutation.mutate({id, type, content}, {
                                onSuccess: () => setShowSave({show: false, id: ''})
                            });
                        } else {
                            setShowSave({show: true, id: isOwned ? (id || '') : ''});
                        }
                    }}
                    options={options.map((opt) => ({
                        label: opt.label,
                        leftSection: opt.icon,
                        disabled: opt.disabled,
                        onClick: opt.onClick
                    }))}
                    menuWidth={200}
                    menuTriggerLabel="Show save actions"
                >
                    {!id ? labels.create : (isOwned ? labels.save : labels.duplicate)}
                </SplitButton>
            ) : (
                <Button leftSection={<FloppyDiskIcon size={16}/>} onClick={() => setShowSave({show: true, id: isOwned ? (id || '') : ''})} disabled={isOverUse}>
                    {labels.create}
                </Button>
            )}
            <Button leftSection={<FolderOpenIcon size={16}/>} onClick={() => setShowLoad(true)}>{labels.load}</Button>
            </Row>

            {showSave.show && <SaveModal
                id={showSave.id}
                onHide={() => setShowSave({show: false, id: ''})}
                onSave={(name: string, isPublic: boolean, id?: string) => {
                    saveMutation.mutate({id, type, name, content, isPublic}, {
                        onSuccess: () => setShowSave({show: false, id: ''})
                    });
                }}
                initialName={savedData?.name}
                initialIsPublic={savedData?.isPublic}
                labels={labels}
            />}

            {showLoad && <LoadModal onHide={() => setShowLoad(false)} type={type} count={count} itemPath={basePath} labels={labels} maxSavedItems={maxSavedItems}/>}

        </>
    )


}


function SaveModal({onHide, onSave, id, initialName, initialIsPublic, labels}: SaveModalProps) {
    const [showModal, setShowModal] = useState<boolean>(!id);
    const [name, setName] = useState<string>(initialName ?? '');
    const [isPublic, setIsPublic] = useState<boolean>(initialIsPublic ?? false);

    useEffect(() => {
        if (typeof initialName !== 'undefined' && typeof initialIsPublic !== 'undefined') {
            setName(initialName);
            setIsPublic(initialIsPublic);
            setShowModal(true);
        }
    }, [initialName, initialIsPublic])


    const handleSubmit = () => {
        onSave(name, isPublic, id);
    };

    const header = id ? labels.updateTitle : labels.createTitle;


    return (
        <Modal onClose={onHide} title={header} opened={showModal}>
            <Row align="center" mb={3}>
                <Col cols={1}>
                    <Label htmlFor="name" mb={0} mt={0}>Name</Label>
                </Col>
                <Col>
                    <TextInput id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Name"/>
                </Col>
            </Row>
            <Row align="center">
                <Col cols={1}>
                    <Label htmlFor="isPublic" mb={0} mt={0}>Public:</Label>
                </Col>
                <Col>
                    <Switch id="isPublic" checked={isPublic} onChange={e => setIsPublic(e.currentTarget.checked)}/>
                </Col>
            </Row>

            <div style={{marginTop: '1rem'}}>
                <Button onClick={handleSubmit} leftSection={<FloppyDiskIcon size={16}/>}>Save</Button>
            </div>

        </Modal>
    )
}

function LoadModal({type, onHide, count, itemPath, labels, maxSavedItems}: LoadModalProps & {itemPath: string}) {
    const {data, isPending} = useQuery<any[]>({
        queryFn: async () => {
            const req = await api.get(`/persistence/${type}/saved`)
            return req.data;
        },
        queryKey: ['persistence', type]
    })
    const {user} = useUser();


    let label = '';
    if (typeof count === 'undefined') {
        label = '(ERR)'
    } else if (!(user.subLevel > 0)) {
        label = `(${count}/${maxSavedItems})`
    }

    const header = <>{labels.loadedListTitle} {label}</>

    if (data) {
        return (
            <Drawer onClose={onHide} opened={!isPending} position="right" title={header}>
                {data.length === 0 ? <i>{labels.emptyList}</i> : null}
                {data?.map(row => <LoadRow key={row.id} row={row} itemPath={itemPath} onHide={onHide}/>)}
            </Drawer>
        )
    }
}

function LoadRow({row, itemPath, onHide}: Omit<LoadRowProps, 'type'> & {itemPath: string}) {
    const navigate = useNavigate();

    return (
        <Row mb={2} align="center">
            <Col grow={1}>{row.name}</Col>
            <Col>
                <ActionIcon variant="subtle" radius="xl" onClick={() => {
                navigate(`${itemPath}/${row.id}`)
                onHide();
                }}>
                <UploadSimpleIcon size={16}/>
            </ActionIcon>
            </Col>
        </Row>
    )
}
